import * as React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { getApiErrorCode, getApiErrorMessage } from "api/apiError";
import { Alert, Button, FormField, Input, notify, PasswordInput } from "Components/ui";
import { Mail } from "Components/ui/icons";
import type { ProfileData } from "../data";

// Same rules as the server (validations/profileValidation.js). The username and email are only
// checked when they change, so an older account's unusual username doesn't block a name change.
const userNameRule = z
  .string()
  .trim()
  .min(3, "Use at least 3 characters")
  .max(40, "Keep it under 40 characters")
  .regex(/^[A-Za-z0-9._-]+$/, "Use letters, numbers, dots, dashes or underscores");
const emailRule = z.string().trim().email("Enter a valid email address").max(254);

interface SavedDetails {
  name: string;
  userName: string;
  email: string;
}

const buildSchema = (saved: SavedDetails) =>
  z
    .object({
      name: z.string().trim().min(1, "Enter your name").max(80, "Keep it under 80 characters"),
      userName: z.string(),
      email: z.string(),
      currPassword: z.string(),
    })
    .superRefine((values, ctx) => {
      const checks: Array<["userName" | "email", boolean, z.ZodTypeAny]> = [
        ["userName", values.userName.trim() !== saved.userName, userNameRule],
        ["email", values.email.trim().toLowerCase() !== saved.email.toLowerCase(), emailRule],
      ];
      checks.forEach(([field, changed, rule]) => {
        const result = changed ? rule.safeParse(values[field]) : null;
        if (result && !result.success) {
          ctx.addIssue({ code: "custom", path: [field], message: result.error.issues[0].message });
        }
      });
    });

type DetailsValues = z.infer<ReturnType<typeof buildSchema>>;

interface DetailsFormProps {
  profile: ProfileData;
  /** Called after saving or cancelling; the drawer shows the details again. */
  onDone: () => void;
}

/**
 * Name, username and email, edited in place in the profile drawer. The username and email are
 * what you sign in with, so changing them asks for your password.
 */
const DetailsForm: React.FC<DetailsFormProps> = ({ profile, onDone }) => {
  const { user, saveDetails } = profile;
  const [formError, setFormError] = React.useState<string | null>(null);
  const saved = React.useMemo<SavedDetails>(
    () => ({
      name: user?.name || "",
      userName: user?.userName || "",
      email: user?.email || "",
    }),
    [user]
  );
  const savedRef = React.useRef(saved);
  savedRef.current = saved;

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<DetailsValues>({
    // Built per submit so it compares against the details as saved right now.
    resolver: (values, context, options) =>
      zodResolver(buildSchema(savedRef.current))(values, context, options),
    defaultValues: { ...saved, currPassword: "" },
  });

  const userName = watch("userName");
  const email = watch("email");
  const signInChanged =
    userName.trim().toLowerCase() !== saved.userName.toLowerCase() ||
    email.trim().toLowerCase() !== saved.email.toLowerCase();

  const onSubmit = async (values: DetailsValues) => {
    setFormError(null);
    const input: { name?: string; userName?: string; email?: string; currPassword?: string } = {};
    if (values.name.trim() !== saved.name) input.name = values.name.trim();
    if (values.userName.trim() !== saved.userName) input.userName = values.userName.trim();
    if (values.email.trim().toLowerCase() !== saved.email.toLowerCase()) {
      input.email = values.email.trim();
    }
    if (!Object.keys(input).length) {
      onDone();
      return;
    }
    if (input.userName !== undefined || input.email !== undefined) {
      if (!values.currPassword) {
        setError("currPassword", { message: "Enter your current password" });
        return;
      }
      input.currPassword = values.currPassword;
    }
    try {
      await saveDetails(input);
      notify.success("Details saved");
      onDone();
    } catch (err) {
      const code = getApiErrorCode(err);
      if (code === "AUTH_PASSWORD_MISMATCH" || code === "PROFILE_PASSWORD_REQUIRED") {
        setError("currPassword", { message: getApiErrorMessage(err) });
      } else if (code === "PROFILE_USERNAME_TAKEN") {
        setError("userName", { message: getApiErrorMessage(err) });
      } else if (code === "PROFILE_EMAIL_TAKEN") {
        setError("email", { message: getApiErrorMessage(err) });
      } else {
        setFormError(getApiErrorMessage(err, "Your details couldn't be saved."));
      }
    }
  };

  return (
    <form noValidate onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      {formError && <Alert tone="danger">{formError}</Alert>}
      <FormField
        label="Name"
        hint="Shown across SAP and in the audit log."
        error={errors.name?.message}
      >
        <Input autoComplete="name" autoFocus {...register("name")} />
      </FormField>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <FormField label="Username" error={errors.userName?.message}>
          <Input autoComplete="username" spellCheck={false} {...register("userName")} />
        </FormField>
        <FormField label="Email" error={errors.email?.message}>
          <Input
            type="email"
            autoComplete="email"
            leading={<Mail />}
            spellCheck={false}
            {...register("email")}
          />
        </FormField>
      </div>
      {signInChanged && (
        <FormField
          label="Current password"
          hint="You sign in with your username or email, so changing them needs your password."
          error={errors.currPassword?.message}
        >
          <PasswordInput autoComplete="current-password" {...register("currPassword")} />
        </FormField>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={onDone} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" loading={isSubmitting}>
          Save details
        </Button>
      </div>
    </form>
  );
};

export default DetailsForm;
